import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import {
  AbcArenaService,
  ArenaPlugin,
  ArenaLayoutPlugin,
  IArenaLayoutPlugin,
  IArenaLayoutPluginNotificationInfo,
  NavigationLayoutViewModel,
  AdditionalHigonAction,
  HigonAction,
  HigonImageType,
  IArenaLayout,
} from '@barthauer/abc/arena';
import {
  ICoreDataEntity,
  SendNotificationModel,
  NotificationData,
  ExecuteQueryDocumentDef,
} from '@barthauer/abc/core/data';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { BceService } from '@barthauer/abc/management/configuration-explorer';
import { BasysArenaComponent } from '@barthauer/basys/arena';
import { AppService } from './app.service';
import { DataService, OT } from './services/data.service';
import { TicketNotificationData } from './viewModels/tickets.viewModel';
import { MobileOrderViewModel, Order } from '@barthauer/abc/maintenance/opera/core';
import { AbcSequencingGridComponent } from '../../../../../libs/abc/ui-components/src/lib/specialized/abc-sequencing-grid/src';
import { ComponentRef, ViewContainerRef } from '@angular/core';
import {
  _AbcGridColumn,
  AbcContainerService,
  AbcWindowComponent,
  IWindow,
  WindowStyle,
} from '../../../../../libs/abc/ui-components/src';
import { WorkService, MtWork as ImMtWork } from '../../../../../libs/basys/opera/wizards/src';
import {
  NotificationsService,
  PushAdapter,
  PushAdapterErrorType,
  PushAdapterSettings,
} from '../../../../../libs/abc/core';
import { BooleanMenuSetting } from '../../../../../libs/abc/arena/src';

const OPERA_NAME = 'Map';
const DELETE_ORDER_INCL_WORKS_BCE_DOC = {
  ID: '3ffa4ee5-6f31-4a26-acbc-c7858da9e29a',
  PARAMETER_NAME: 'OrderId',
};
const DELETE_WORKS_INCL_TASKS_BCE_DOC = {
  ID: '8040bdef-06e1-44d0-a9c4-eca785e06025',
  PARAMETER_NAME: 'Selection',
};
const UPDATE_LEAD_TIME_INCL_BCE_DOC = {
  ID: 'bf9f145c-2d94-433b-8173-8e7bc9d1ef7c',
  PARAMETER_NAME: 'Selection',
};

const SORTING_DIALOG_ID = 'SortingDialogId';

interface IOperaManagerPluginContext {
  readonly core: AbcCore;
  readonly messageBoxService: MessageBoxService;
  readonly protocolService: ProtocolViewerService;
  readonly abcArenaService: AbcArenaService;
  readonly appService: AppService;
  readonly dataService: DataService;
  readonly bceService: BceService;
  readonly basysArenaCpt: BasysArenaComponent;
  readonly abcContainer: AbcContainerService;
  readonly workService: WorkService;
}

export class OperaManagerPlugin extends ArenaPlugin {
  private pushAdapter: PushAdapter;
  constructor(
    private context: IOperaManagerPluginContext,
    private notificationsService: NotificationsService,
  ) {
    super(async (navigationLayout: NavigationLayoutViewModel) => {
      const result: IArenaLayoutPlugin[] = [];
      if (await context.core.objectSpace.is(navigationLayout.objectType, OT.Auftrag.name)) {
        result.push(new AuftragPlugin(context));
      } else if (await context.core.objectSpace.is(navigationLayout.objectType, OT.MtTicket.name)) {
        result.push(new MtTicketPlugin(context));
      } else if (await context.core.objectSpace.is(navigationLayout.objectType, OT.MtTicketTask.name)) {
        result.push(new MtTicketTaskPlugin(context));
      } else if (await context.core.objectSpace.is(navigationLayout.objectType, OT.MtWork.name)) {
        result.push(new MtWorkPlugin(context));
      } else if (await context.core.objectSpace.is(navigationLayout.objectType, OT.MtObjWorkType.name)) {
        result.push(new MtObjWorkTypePlugin(context));
      }
      return result;
    });
  }

  public async init(): Promise<void> {
    await this.initPushNotifications();
  }

  private async initPushNotifications(): Promise<void> {
    try {
      const pushNotificationsSetting = new BooleanMenuSetting();
      pushNotificationsSetting.caption = 'Arena-General-Notification';
      pushNotificationsSetting.name = 'PushNotification';
      pushNotificationsSetting.value = false;
      pushNotificationsSetting.parentVisibleIndex = 1;
      pushNotificationsSetting.visibleIndex = 2;
      // pushNotificationsSetting.onBeforeCancel = (): Promise<void> => {
      //   return Promise.reject(new Error('test error')); // BEC: this is to test the cancel true
      // };

      pushNotificationsSetting.valueChanged = async (valueChangedEvent) => {};

      this.context.basysArenaCpt.additionalMenuSettingItems = [pushNotificationsSetting];

      const pushAdapterSettings: PushAdapterSettings = new PushAdapterSettings();
      pushAdapterSettings.onActiveChanged = (active) => {
        pushNotificationsSetting.value = active;
        //    this.basysMapFrameService.detectChanges();
      };
      pushAdapterSettings.onActivatableChanged = (enabled) => {
        pushNotificationsSetting.enabled = enabled;
        //    this.basysMapFrameService.detectChanges();
      };

      const that = this;
      pushAdapterSettings.onNotificationReceived = (data) => {
        if (that.updateNotificationInfos) {
          that.updateNotificationInfos();
        }
        data.icon = '/assets/resources/opera-manager/appIcon.png';
        return data;
      };

      pushAdapterSettings.onError = (type, err) => {
        switch (type) {
          case PushAdapterErrorType.UnableToSubscribeToNotificationService:
          case PushAdapterErrorType.UnableToSubscribeOnWebApiEndpoint:
            pushNotificationsSetting.errorMessage = this.context.appService.res.get(
              'Push-Messages-Error-UnableToSubscribe',
            );

            // alert(this.context.appService.res.get('Push-Messages-Error-UnableToSubscribe'));
            //this.frameService.openSnackBar(res.get('Push-Messages-Error-UnableToSubscribe'), 2000);
            break;
          case PushAdapterErrorType.TimeoutWhileRequestingPermissions:
            pushNotificationsSetting.errorMessage = this.context.appService.res.get(
              'Push-Messages-Error-TimeoutWhileRequestingPermissions',
            );

            // alert(this.context.appService.res.get('Push-Messages-Error-TimeoutWhileRequestingPermissions'));
            //this.frameService.openSnackBar(res.get('Push-Messages-Error-TimeoutWhileRequestingPermissions'), 2000);
            break;
        }
        console.error(PushAdapterErrorType[type]);
        if (err) {
          console.error(err);
        }
      };

      this.pushAdapter = await this.notificationsService?.tryCreatePushAdapter(
        this.context.core.data,
        'opera',
        undefined,
        'assets/service-workers/sw-opera-manager-notifications-firebase.js',
        pushAdapterSettings,
      );

      if (this.pushAdapter) {
        this.pushAdapter.checkActive();
      }
      if (this.pushAdapter) {
        pushNotificationsSetting.onBeforeSave = async () => {
          try {
            if (!pushNotificationsSetting.value) {
              await this.pushAdapter.activatePushNotifications();
            } else {
              await this.pushAdapter.deactivatePushNotifications();
            }
            return true;
          } catch (error) {
            pushAdapterSettings?.onError(error as PushAdapterErrorType);
            throw error;
          }
        };
      }
    } catch (err) {
      this.context.core.error.publish(err);
    }
  }
}

export abstract class OperaManagerLayoutPluginBase extends ArenaLayoutPlugin {
  constructor(protected readonly context: IOperaManagerPluginContext) {
    super();
  }

  protected async showMessageBoxAndPerformAction(message: string, action: () => Promise<void>) {
    const messageSettings = new MessageSettings();
    messageSettings.mode = MessageMode.Question;
    messageSettings.icon = MessageBoxIcon.Question;
    messageSettings.button = MessageBoxButtons.YesNo;
    messageSettings.message = message;

    const result = await this.context.messageBoxService.showMessageBox(messageSettings).toPromise();
    if (result['result'] === DialogResult.Yes) {
      await action();
    }
  }

  protected async showMessageBoxInfo(message: string) {
    const messageSettings = new MessageSettings();
    messageSettings.mode = MessageMode.Info;
    messageSettings.icon = MessageBoxIcon.Info;
    messageSettings.button = MessageBoxButtons.OK;
    messageSettings.message = message;

    const result = await this.context.messageBoxService.showMessageBox(messageSettings).toPromise();
  }
}

class AuftragPlugin extends OperaManagerLayoutPluginBase {
  private sequencingWindowInstance: AbcWindowComponent | undefined;
  private sequencingInstance: AbcSequencingGridComponent | undefined;

  constructor(context: IOperaManagerPluginContext) {
    super(context);
  }

  public async getAdditionalHigonActions(): Promise<AdditionalHigonAction[]> {
    return [
      {
        objectType: OT.Auftrag.name,
        text: this.context.appService.res.get('ShowOrderInOpera'),
        imageSrc: this.context.appService.res.svgUrl('ShowOrderInOpera-Image'),
        action: async () => await this.setOrderAndOpenOpera(),
      },
      {
        objectType: OT.Auftrag.name,
        text: this.context.appService.res.get('DeleteOrderWithWorks'),
        imageType: HigonImageType.Delete,
        action: async (action: HigonAction) =>
          await this.deleteOrderIncludingWorks(action.dataEntity, action.arenaLayout),
      },
      {
        objectType: OT.Auftrag.name,
        text: this.context.appService.res.get('EditWorksSortOrder'),
        imageType: HigonImageType.DmlQuery,
        action: async (action: HigonAction) => {
          const works: ImMtWork[] = await this.context.workService.queryWorksByOrderId(action.dataEntity.Id);
          await this.openSortOrderDialog(action.dataEntity, action.arenaLayout, works);
        },
      },
    ];
  }

  public async openSortOrderDialog(objectInfo: ICoreDataEntity, arenaLayout: IArenaLayout, works: ImMtWork[]) {
    if (this.sequencingWindowInstance) {
      this.closeSequencingDialog();
    }

    if (!this.sequencingInstance) {
      const window = this.createSequencingDialogWindow(
        AbcSequencingGridComponent,
        SORTING_DIALOG_ID,
        this.context.appService.res.get('SortDialogTitle'),
      );
      this.sequencingWindowInstance = <AbcWindowComponent>window.componentRef.instance;
      this.sequencingWindowInstance.createComponentInstance().subscribe(async (activeComponent: any) => {
        const w = activeComponent.openWindow();
        const dialogInstance = w.instance;
        this.sequencingInstance = <AbcSequencingGridComponent>dialogInstance;
        this.sequencingInstance.sortOrderColumnCaption = this.context.appService.res.get(
          'WorkSortingColumnCaption_Sequence',
        );

        this.sequencingInstance.dataSourceObject = { dataSource: works, gridColumns: this.getDefinedWorkColumns() };
        this.sequencingInstance.saveSorting.subscribe((sortedWorks) => this.saveSortedWorks(sortedWorks, arenaLayout));
        this.sequencingInstance.cancelSorting.subscribe(() => this.closeSequencingDialog());
        this.sequencingWindowInstance!.showLoader = false;
      });
    }
  }

  private async saveSortedWorks(sortedWorks: any[], arenaLayout: IArenaLayout): Promise<void> {
    if (sortedWorks) {
      let beginLoading: { active: boolean } | undefined;
      try {
        beginLoading = arenaLayout.beginLoading();

        const mtWorks: ImMtWork[] = sortedWorks.map((item, index) => {
          const w = item as ImMtWork;
          w.SortOrder = index + 1;
          return w;
        });

        const result = await this.context.workService.updateWorkSortOrders(mtWorks);
        if (result.success) {
          this.showMessageBoxInfo(
            this.context.appService.res.get('WorksSortOrderSaved', result.affectedCount.toString()),
          );
          await arenaLayout.requery();
        } else {
          this.showMessageBoxInfo(this.context.appService.res.get('UpdateErrorMessage', result.error?.message));
        }
      } catch (error) {
        this.context.core.error.publish(error);
        if (!!beginLoading) {
          arenaLayout.endLoading(beginLoading);
        }
      } finally {
        arenaLayout.endLoading(beginLoading);
      }
    }

    this.closeSequencingDialog();
  }

  private closeSequencingDialog(): void {
    if (this.sequencingWindowInstance) {
      this.sequencingWindowInstance.closeDialog();
      this.sequencingWindowInstance = undefined;
    }
    if (this.sequencingInstance) {
      this.sequencingInstance = undefined;
    }
  }

  private getDefinedWorkColumns(): _AbcGridColumn[] {
    let ret: _AbcGridColumn[] = [];
    ret.push({
      dataField: 'ObjectDesignation',
      name: 'Object',
      caption: this.context.appService.res.get('WorkSortingColumnCaption_Object'),
      visible: true,
    });
    ret.push({
      dataField: 'MasterObjectTypeDesignation',
      name: 'MasterObjectTypeDesignation',
      caption: this.context.appService.res.get('WorkSortingColumnCaption_ObjectType'),
      visible: true,
    });
    ret.push({
      dataField: 'Designation',
      name: 'Designation',
      caption: this.context.appService.res.get('WorkSortingColumnCaption_Work'),
      visible: true,
    });
    ret.push({
      dataField: 'Street',
      name: 'Street',
      caption: this.context.appService.res.get('WorkSortingColumnCaption_Street'),
      visible: true,
    });
    ret.push({
      dataField: 'District',
      name: 'District',
      caption: this.context.appService.res.get('WorkSortingColumnCaption_District'),
      visible: true,
    });

    return ret;
  }

  private createSequencingDialogWindow<T>(
    component: T,
    id: string,
    title: string,
  ): {
    componentRef: ComponentRef<unknown>;
    domElement: HTMLElement;
  } {
    const style: WindowStyle = {
      height: '530px',
      width: '800px',
      minHeight: '500px',
      minWidth: '800px',
      maxHeight: '100%',
      maxWidth: '100%',
      position: { my: 'center', at: 'center' },
      shading: true,
      resizable: true,
    };

    const windowAttr: IWindow = {
      id: id,
      label: title,
      showLoader: true,
      storeState: true,
      storeStateKey: id,
      maximizeButton: true,
      minimizeButton: true,
      component: component,
      isInMobile: false,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };

    let abcWindow: ViewContainerRef;
    return this.context.abcContainer.createComponent(abcWindow, AbcWindowComponent, windowAttr);
  }

  private async setOrderAndOpenOpera(): Promise<void> {
    if (
      await this.context.core.objectSpace.isMatch(
        this.context.abcArenaService.currentDataEntity?.ObjectType,
        OT.Auftrag.name,
      )
    ) {
      const order = new Order({ Id: this.context.abcArenaService.currentDataEntity.Id });
      const mobileOrder = new MobileOrderViewModel(order, false);
      this.context.appService.order = mobileOrder;
      this.context.basysArenaCpt.openNavigationGroup(OPERA_NAME);
    }
  }

  private async deleteOrderIncludingWorks(entity: ICoreDataEntity, arenaLayout: IArenaLayout): Promise<void> {
    if (await this.context.core.objectSpace.isMatch(entity?.ObjectType, OT.Auftrag.name)) {
      const orderId = entity.Id;
      const executeQueryDocumentDef: ExecuteQueryDocumentDef = new ExecuteQueryDocumentDef();
      executeQueryDocumentDef.Id = DELETE_ORDER_INCL_WORKS_BCE_DOC.ID;
      executeQueryDocumentDef.Parameters = [
        {
          Name: DELETE_ORDER_INCL_WORKS_BCE_DOC.PARAMETER_NAME,
          Value: orderId,
        },
      ];

      await this.showMessageBoxAndPerformAction(
        this.context.appService.res.get('ConfirmDeleteOrderIncludingWorksMessage'),
        async () => {
          let beginLoading: { active: boolean } | undefined;
          let order: Object | undefined;

          try {
            beginLoading = arenaLayout.beginLoading();

            order = await this.context.core.data.executeQueryDocument(executeQueryDocumentDef).toPromise();
          } catch (error) {
            this.context.core.error.publish(error);
            if (!!beginLoading) {
              arenaLayout.endLoading(beginLoading);
            }
          } finally {
            if (!!order && !CompareHelper.isEmptyObject(order)) {
              await this.context.protocolService.openProtocolViewer(
                order,
                undefined,
                undefined,
                this.context.appService.res.get('ProtocolViewerTitleAfterDeletion'),
                undefined,
                async () => {
                  await arenaLayout.removeGridObjectById(orderId);
                },
              );
            }

            arenaLayout.endLoading(beginLoading);
          }
        },
      );
    }
  }
}

class MtTicketPlugin extends OperaManagerLayoutPluginBase {
  constructor(context: IOperaManagerPluginContext) {
    super(context);
  }

  public async getNotificationInfo(): Promise<IArenaLayoutPluginNotificationInfo> {
    return {
      info: this,
      caption: this.context.appService.res.get('TicketNotificationsCaption'),
      filter: OT.MtTicket.filters.withActiveReminers,
      resetNotificationAction: async (entity: ICoreDataEntity) => {
        await this.context.dataService.setRemindersActive(entity, false);
      },
    };
  }
}

class MtTicketTaskPlugin extends OperaManagerLayoutPluginBase {
  constructor(context: IOperaManagerPluginContext) {
    super(context);
  }

  public async getAdditionalHigonActions(): Promise<AdditionalHigonAction[]> {
    return [
      {
        objectType: OT.MtTicketTask.name,
        text: this.context.appService.res.get('PublishTicket'),
        imageSrc: this.context.appService.res.svgUrl('ShowOrderInOpera-Image'),
        action: async (action: HigonAction) => await this.addTicketTaskReminderAndPublish(action.dataEntity),
      },
    ];
  }

  private async addTicketTaskReminderAndPublish(entity: ICoreDataEntity): Promise<void> {
    if (await this.context.core.objectSpace.isMatch(entity?.ObjectType, OT.MtTicketTask.name)) {
      this.showMessageBoxAndPerformAction(this.context.appService.res.get('ConfirmPublishTicketMessage'), async () => {
        try {
          const ticketTaskId = entity.Id;
          await this.context.dataService.insertOrUpdateReminder(OT.MtTicketTask.name, ticketTaskId);
          const ticketData: TicketNotificationData =
            await this.context.dataService.queryTicketNotificationData(ticketTaskId);
          await this.sendTicketTaskNotification(ticketData);
        } catch (error) {
          this.context.core.error.publish(error);
        }
      });
    }
  }

  private async sendTicketTaskNotification(ticketData: TicketNotificationData): Promise<void> {
    const model: SendNotificationModel = new SendNotificationModel();
    model.UserOrGroupNames = [ticketData.TicketTaskResourceLogin];

    const data: NotificationData = new NotificationData();
    data.title = this.context.appService.res.get(
      'TicketMessageTitle',
      ticketData.TicketNumber,
      ticketData.TicketDesignation,
    );

    let body: string = this.context.appService.res.get('TicketMessageBody', ticketData.TicketTaskDesignation);
    if (ticketData.TicketPriority) {
      body = this.context.appService.res.get('TicketMessagePriority', ticketData.TicketPriority) + '\n' + body;
    }
    data.body = body;

    model.Data.data = data;

    await this.context.core.data.sendNotification(model).toPromise();
  }
}

class MtWorkPlugin extends OperaManagerLayoutPluginBase {
  constructor(context: IOperaManagerPluginContext) {
    super(context);
  }

  public async getAdditionalHigonActions(): Promise<AdditionalHigonAction[]> {
    return [
      {
        objectType: OT.MtWork.name,
        text: this.context.appService.res.get('DeleteWorksWithTasks'),
        imageType: HigonImageType.Delete,
        action: async (action: HigonAction) => {
          const entities: ICoreDataEntity[] = await action.arenaLayout.getCurrentOrFilteredDataEntities(true);
          await this.deleteWorksIncludingTasks(entities, action.arenaLayout);
        },
      },
    ];
  }

  private async deleteWorksIncludingTasks(entities: ICoreDataEntity[], arenaLayout: IArenaLayout): Promise<void> {
    if (entities?.length > 0) {
      await this.showMessageBoxAndPerformAction(
        this.context.appService.res.get('ConfirmDeleteWorkIncludTasksMessage'),
        async () => {
          const parameters: ParameterValue[] = [
            {
              Name: DELETE_WORKS_INCL_TASKS_BCE_DOC.PARAMETER_NAME,
              Value: entities.map((_) => _.Id),
            },
          ];
          let beginLoading: { active: boolean } | undefined;

          try {
            beginLoading = arenaLayout.beginLoading();

            await this.context.bceService.executeDmlQueryDocument(DELETE_WORKS_INCL_TASKS_BCE_DOC.ID, '', parameters);

            await arenaLayout.requery();
          } catch (error) {
            this.context.core.error.publish(error);
            if (!!beginLoading) {
              arenaLayout.endLoading(beginLoading);
            }
          } finally {
            arenaLayout.endLoading(beginLoading);
          }
        },
      );
    }
  }
}

class MtObjWorkTypePlugin extends OperaManagerLayoutPluginBase {
  constructor(context: IOperaManagerPluginContext) {
    super(context);
  }

  public async getAdditionalHigonActions(): Promise<AdditionalHigonAction[]> {
    return [
      {
        objectType: OT.MtObjWorkType.name,
        text: 'Planungsvorlauf ändern',
        imageType: HigonImageType.DmlQuery,
        action: async (action: HigonAction) => {
          const entities: ICoreDataEntity[] = await action.arenaLayout.getCurrentOrFilteredDataEntities(true);
          await this.updateLeadTime(entities, action.arenaLayout);
        },
      },
    ];
  }

  private async updateLeadTime(entities: ICoreDataEntity[], arenaLayout: IArenaLayout): Promise<void> {
    if (entities?.length > 0) {
      await this.showMessageBoxAndPerformAction(
        this.context.appService.res.get('ConfirmUpdateLeadTimeMessage'),
        async () => {
          const parameters: ParameterValue[] = [
            {
              Name: UPDATE_LEAD_TIME_INCL_BCE_DOC.PARAMETER_NAME,
              Value: entities.map((_) => _.Id),
            },
          ];

          let beginLoading: { active: boolean } | undefined;
          try {
            beginLoading = arenaLayout.beginLoading();
            await this.context.bceService.executeDmlQueryDocumentWithParameters(
              UPDATE_LEAD_TIME_INCL_BCE_DOC.ID,
              '',
              parameters,
            );

            await arenaLayout.requery();
          } catch (error) {
            this.context.core.error.publish(error);
            if (!!beginLoading) {
              arenaLayout.endLoading(beginLoading);
            }
          } finally {
            arenaLayout.endLoading(beginLoading);
          }
        },
      );
    }
  }
}
